/**
 * Owner: Masa Bayu
 * File: qxeditor-session-store.ts
 * Date:Time: 2026-10-04 22:11
 * Description: Persist imported QXEditor session across refresh (IndexedDB).
 */

import { deserializeQXWDocument, serializeQXWDocument, type QXWDocument } from '@qx/qxw-core';
import type { ReconstructionReport } from '../../../../packages/qx-editor/src/types';

const DB_NAME = 'qxeditor-decision-b-session';
const DB_VERSION = 1;
const STORE = 'session';
const KEY = 'active';

export type QxeditorSessionSnapshot = {
  readonly document: QXWDocument;
  readonly sourceLabel: string | null;
  readonly pdfBytes: Uint8Array | null;
  readonly reconstruction: ReconstructionReport | null;
};

type StoredRow = {
  readonly documentWire: string;
  readonly sourceLabel: string | null;
  readonly pdfBytes: ArrayBuffer | null;
  readonly reconstruction: ReconstructionReport | null;
  readonly savedAt: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Session store did not open'));
  });
}

function toArrayBuffer(bytes: Uint8Array | null): ArrayBuffer | null {
  if (!bytes || bytes.byteLength === 0) return null;
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function fromArrayBuffer(buffer: ArrayBuffer | null | undefined): Uint8Array | null {
  if (!buffer || buffer.byteLength === 0) return null;
  return new Uint8Array(buffer.slice(0));
}

/** Reads the last imported manuscript + PDF bytes for this browser. */
export async function readQxeditorSession(): Promise<QxeditorSessionSnapshot | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await openDb();
  try {
    const row = await new Promise<StoredRow | null>((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      request.onsuccess = () => {
        const value = request.result;
        resolve(value && typeof value === 'object' ? (value as StoredRow) : null);
      };
      request.onerror = () => reject(request.error ?? new Error('Session did not read'));
    });
    if (!row || typeof row.documentWire !== 'string') return null;
    const parsed = deserializeQXWDocument(row.documentWire);
    if (!parsed.ok) return null;
    return {
      document: parsed.document,
      sourceLabel: typeof row.sourceLabel === 'string' ? row.sourceLabel : null,
      pdfBytes: fromArrayBuffer(row.pdfBytes),
      reconstruction: row.reconstruction ?? null,
    };
  } finally {
    db.close();
  }
}

/** Writes the active import so refresh reopens the same manuscript. */
export async function writeQxeditorSession(snapshot: QxeditorSessionSnapshot): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  if (snapshot.document.pages.length === 0) {
    await clearQxeditorSession();
    return;
  }
  const row: StoredRow = {
    documentWire: serializeQXWDocument(snapshot.document),
    sourceLabel: snapshot.sourceLabel,
    pdfBytes: toArrayBuffer(snapshot.pdfBytes),
    reconstruction: snapshot.reconstruction,
    savedAt: Date.now(),
  };
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(STORE, 'readwrite').objectStore(STORE).put(row, KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error('Session did not save'));
    });
  } finally {
    db.close();
  }
}

/** Clears the saved import session. */
export async function clearQxeditorSession(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error ?? new Error('Session did not clear'));
    });
  } finally {
    db.close();
  }
}
